/**
 * src/pages/WhatIsConsciousness/consciousnessData.tsx
 * Structured data, citations, brain regions, theories, and unanswered questions.
 */

import React from "react";
import type { SourceRef, ExpandItem } from "@/lib/originBeyond/types";
export const CONSCIOUSNESS_SOURCES: SourceRef[] = [{
  id: 1,
  citation: "Chalmers, D. J. (1995). Facing up to the problem of consciousness. Journal of Consciousness Studies, 2(3), 200–219.",
  url: "https://consc.net/papers/facing.html"
}, {
  id: 2,
  citation: "Nagel, T. (1974). What is it like to be a bat? The Philosophical Review, 83(4), 435–450.",
  url: "https://www.jstor.org/stable/2183914"
}, {
  id: 3,
  citation: "Azevedo, F. A. C., et al. (2009). Equal numbers of neuronal and nonneuronal cells make the human brain an isometrically scaled-up primate brain. Journal of Comparative Neurology, 513(5), 532–541.",
  url: "https://doi.org/10.1002/cne.21974"
}, {
  id: 4,
  citation: "Koch, C., Massimini, M., Boly, M., & Tononi, G. (2016). Neural correlates of consciousness: progress and problems. Nature Reviews Neuroscience, 17(5), 307–321.",
  url: "https://doi.org/10.1038/nrn.2016.22"
}, {
  id: 5,
  citation: "Dehaene, S., & Changeux, J. P. (2011). Experimental and theoretical approaches to conscious processing. Neuron, 70(2), 200–227.",
  url: "https://doi.org/10.1016/j.neuron.2011.03.018"
}, {
  id: 6,
  citation: "Tononi, G., Boly, M., Massimini, M., & Koch, C. (2016). Integrated information theory: from consciousness to its physical substrate. Nature Reviews Neuroscience, 17(7), 450–461.",
  url: "https://doi.org/10.1038/nrn.2016.44"
}, {
  id: 7,
  citation: "Roediger, H. L., & McDermott, K. B. (1995). Creating false memories: Remembering words not presented in lists. Journal of Experimental Psychology: Learning, Memory, and Cognition, 21(4), 803–814.",
  url: "https://doi.org/10.1037/0278-7393.21.4.803"
}, {
  id: 8,
  citation: "Owen, A. M., et al. (2006). Detecting awareness in the vegetative state. Science, 313(5792), 1402–1402.",
  url: "https://doi.org/10.1126/science.1130197"
}, {
  id: 9,
  citation: "Stanford Encyclopedia of Philosophy. (2020). Consciousness & Personal Identity entries.",
  url: "https://plato.stanford.edu/entries/consciousness/"
}, {
  id: 10,
  citation: "Low, P., et al. (2012). The Cambridge Declaration on Consciousness. Francis Crick Memorial Conference on Consciousness in Human and non-Human Animals, Cambridge, UK.",
  url: "https://fcmconference.org/"
}];
export interface BrainRegionData {
  id: string;
  name: string;
  subTitle: string;
  evidenceLevel: "established" | "research";
  summary: string;
  roleDescription: string;
  consciousnessLink: string;
  empiricalFact: string;
  refs: number[];
}
export const BRAIN_REGIONS: BrainRegionData[] = [{
  id: "prefrontal",
  name: "Prefrontal Cortex",
  subTitle: "Executive evaluation, working memory & metacognition",
  evidenceLevel: "research",
  summary: "Anterior frontal lobe supporting executive planning, attention steering, and introspective report.",
  roleDescription: "Coordinates working memory, selective attention, decision planning, and goal-directed actions.",
  consciousnessLink: "Heavily featured in Global Neuronal Workspace and Higher-Order thought models. Lesions rarely eliminate basic sensory awareness, but impair metacognitive confidence and flexible behavioural report.",
  empiricalFact: "Comprises roughly 29% of the human cerebral cortex, yet bilateral frontal lobotomies preserved patients' rudimentary sensory consciousness.",
  refs: [4, 5]
}, {
  id: "visual",
  name: "Visual Cortex (Occipital)",
  subTitle: "Primary & extrastriate sensory processing",
  evidenceLevel: "established",
  summary: "Posterior sensory hub transforming retinal signals into maps of orientation, colour, motion, and form.",
  roleDescription: "Processes raw visual inputs (V1 through V5) to parse optical scenes, geometric contours, and depth.",
  consciousnessLink: "Damage to primary visual cortex produces 'blindsight'—patients report total subjective blindness in a visual field yet can still detect movement above chance. This proves sensory processing can occur without subjective experience.",
  empiricalFact: "Active V1 activity alone is generally insufficient for conscious visual perception; feedback from higher extrastriate regions is required.",
  refs: [4]
}, {
  id: "thalamus",
  name: "Thalamus & Reticular Core",
  subTitle: "Sensory gateway & cortico-thalamic synchrony",
  evidenceLevel: "established",
  summary: "Deep central hub relaying sensory signals and pacing oscillatory dialogue across the cortex.",
  roleDescription: "Routes nearly all incoming sensory signals (except olfactory) into corresponding cortical zones.",
  consciousnessLink: "Acts as a vital switchboard. Bilateral damage to specific intralaminar thalamic nuclei produces profound coma or vegetative states, suggesting it maintains the baseline 'level' or arousal enabling conscious contents.",
  empiricalFact: "Thalamocortical loops oscillate at gamma (30–80 Hz) frequencies during focused waking awareness and synchronise distant cortical assemblies.",
  refs: [4, 5]
}, {
  id: "brainstem",
  name: "Brainstem & Ascending Reticular Activating System",
  subTitle: "Autonomic survival & baseline arousal",
  evidenceLevel: "established",
  summary: "Caudal stalk controlling cardiorespiratory rhythms, sleep-wake cycles, and tonic neuromodulation.",
  roleDescription: "Regulates involuntary vital reflexes, autonomic vascular control, and supplies ascending acetylcholine, noradrenaline, and dopamine.",
  consciousnessLink: "Enables wakefulness rather than the rich content of consciousness. Even tiny lesions in the dorsal brainstem cause immediate and irreversible coma, demonstrating that brainstem arousal is an indispensable prerequisite for any conscious state.",
  empiricalFact: "Provides the indispensable physiological fuel: without ascending monoaminergic tone, the higher cerebral cortex falls permanently dormant.",
  refs: [4]
}, {
  id: "hippocampus",
  name: "Hippocampus & Medial Temporal Lobe",
  subTitle: "Episodic memory consolidation & mental time travel",
  evidenceLevel: "established",
  summary: "Seahorse-shaped deep structure crucial for forming declarative memories and spatial mapping.",
  roleDescription: "Encodes new biographical episodes, transfers short-term representations to the neocortex, and binds contextual features.",
  consciousnessLink: "Bilateral resection (as in famous patient H.M.) produces severe anterograde amnesia. Crucially, H.M. retained vivid moment-to-moment consciousness, proving episodic memory is not necessary for having immediate subjective experiences.",
  empiricalFact: "Stores cognitive coordinate maps via place cells and grid-cell networks while leaving the immediate conscious 'now' intact when damaged.",
  refs: [7, 9]
}, {
  id: "cerebellum",
  name: "Cerebellum (Little Brain)",
  subTitle: "Sensory-motor fine-tuning & algorithmic calibration",
  evidenceLevel: "established",
  summary: "Dense posterior structure containing the majority of all neurons in the human brain.",
  roleDescription: "Calibrates precise motor timing, posture balance, fluid coordination, and automated motor sequences.",
  consciousnessLink: "A profound scientific puzzle: holds roughly 69 billion of the brain's ~86 billion neurons (about 80%), yet complete congenital absence or massive damage causes ataxia without diminishing conscious awareness. Proves neuron count alone does not create consciousness.",
  empiricalFact: "Its feedforward, modular microcircuitry lacks the dense recurrent loops found in the cerebral cortex, suggesting recurrent feedback is essential for experience.",
  refs: [3, 6]
}, {
  id: "dmn",
  name: "Default Mode Network (Distributed)",
  subTitle: "Introspection, daydreaming & narrative selfhood",
  evidenceLevel: "research",
  summary: "Large-scale interconnected network (precuneus, posterior cingulate, medial prefrontal) active at rest.",
  roleDescription: "Activates during autobiographical reflection, theory of mind, imagining the future, and spontaneous mind-wandering.",
  consciousnessLink: "Strongly tied to the narrative 'I'—the continuous autobiographical self. Psychedelics (like psilocybin) disrupt DMN functional connectivity, correlating with temporary 'ego dissolution' where sensory awareness remains but the boundary of self dissolves.",
  empiricalFact: "Exhibits anti-correlated activity with task-positive attentional networks; when external problem-solving demands rise, DMN activity drops.",
  refs: [4, 9]
}];
export const UNANSWERED_CONSCIOUSNESS_QUESTIONS: ExpandItem[] = [{
  id: "hard-problem-deep",
  title: "Why is there any subjective experience at all?",
  level: "philosophy",
  summary: "The root explanatory gap between electrochemistry and subjective feelings.",
  body: <div className="space-y-3 font-sans">
        <p>
          We can map sensory pathways, quantify membrane action potentials, and trace neuromodulator cascades. Yet nothing in physical equations logically entails why these mechanical processes should <em>feel</em> like anything from the inside.
        </p>
        <p>
          A complex supercomputer could hypothetically register photons, classify objects, and navigate obstacles entirely in the dark, without any inner light of awareness. Why physical complexity is accompanied by an inner life remains the foundational riddle of philosophy of mind.
        </p>
      </div>,
  refs: [1, 2]
}, {
  id: "animal-consciousness",
  title: "Which non-human animals are conscious?",
  level: "research",
  summary: "From primates and cetaceans to octopuses and bees.",
  body: <div className="space-y-3 font-sans">
        <p>
          The 2012 Cambridge Declaration on Consciousness concluded that non-human animals (including all mammals, birds, and octopuses) possess the neuroanatomical substrates capable of generating conscious states.
        </p>
        <p>
          However, drawing precise boundaries remains contested: do insects feel pain or subjective distress? Octopuses have two-thirds of their neurons distributed in their arms, representing a completely alien evolutionary branch of complex neural architecture. Science is shifting from 'are they conscious?' to 'what is the quality and scope of their experience?'
        </p>
      </div>,
  refs: [10]
}, {
  id: "ai-consciousness",
  title: "Can artificial intelligence systems become conscious?",
  level: "speculative",
  summary: "Distinguishing simulated intelligence from genuine sentience.",
  body: <div className="space-y-3 font-sans">
        <p>
          Current Large Language Models manipulate linguistic tokens with remarkable sophistication, but consensus neurobiology views them as feedforward statistical algorithms devoid of inner feeling, bodily embodiment, or homeostatic drive.
        </p>
        <p>
          Whether consciousness requires biological substrates (carbon chemistry, metabolic vulnerability), specific causal architectures (such as high integrated information, Φ), or if functional software running on silicon can eventually develop true subjectivity remains an unsettled, highly speculative question.
        </p>
      </div>,
  refs: [6, 9]
}, {
  id: "unresponsive-patients",
  title: "Can we detect covert consciousness in unresponsive patients?",
  level: "research",
  summary: "Functional MRI and EEG breakthroughs in disorders of consciousness.",
  body: <div className="space-y-3 font-sans">
        <p>
          In a landmark 2006 study, Adrian Owen and colleagues placed a clinically diagnosed 'vegetative' patient in an fMRI scanner and asked her to imagine playing tennis or walking through her house. Her motor cortex and spatial navigation circuits lit up identically to healthy volunteers.
        </p>
        <p>
          This revealed 'covert consciousness' (cognitive motor dissociation)—patients fully aware internally yet completely unable to command their peripheral musculature. Developing portable EEG markers (like the Perturbational Complexity Index) is an urgent area of translational neuroscience.
        </p>
      </div>,
  refs: [8]
}, {
  id: "sleep-anaesthesia",
  title: "What happens to consciousness in dreamless sleep and anaesthesia?",
  level: "established",
  summary: "The reversible collapse of thalamocortical integration.",
  body: <div className="space-y-3 font-sans">
        <p>
          During general anaesthesia and slow-wave (non-REM) sleep, neurons in the cortex do not switch off; in fact, their overall metabolic rate only drops by about 30–50%. Yet conscious experience vanishes or fragments entirely.
        </p>
        <p>
          Electrophysiological probing shows that the cortex loses its ability to integrate information: a localized transcranial magnetic stimulation pulse either fails to propagate (local collapse) or triggers a stereotypical, non-differentiated slow wave across the whole brain. Consciousness requires both high differentiation (variety of states) and high integration (global connectivity).
        </p>
      </div>,
  refs: [4, 6]
}];